<template>
  <div class="home">
    <!-- Navbar -->
    <Navbar :currentLang="currentLang" @change-lang="changeLang" />

    <!-- Hero Section -->
    <section class="hero">
      <div class="hero-bg-overlay"></div>
      <div class="hero-inner">
        <!-- Badge -->
        <div class="hero-badge" data-aos="fade-down">
          <span class="badge-dot"></span>
          <span>{{ t.home.trusted }}</span>
        </div>
        <div class="hero-body">
          <div class="hero-content" data-aos="fade-right">
            <h1>
              <span class="hero-eyebrow">{{ t.hero.title1 }}</span>
              <span class="hero-title-2">{{ t.hero.title2 }}</span>
            </h1>
            <p class="hero-description">{{ t.hero.description }}</p>
            <div class="hero-cta-group">
              <button class="btn-primary" @click="scrollToContact">
                {{ t.hero.btn }}
                <svg class="btn-arrow" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clip-rule="evenodd"/></svg>
              </button>
              <button class="btn-ghost" @click="scrollToContact">
                <svg class="whatsapp-icon" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.119.554 4.107 1.523 5.83L0 24l6.338-1.498A11.96 11.96 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.854 0-3.59-.5-5.086-1.373l-.361-.214-3.762.889.941-3.667-.235-.375A9.945 9.945 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/></svg>
                {{ t.home.talkExpert }}
              </button>
            </div>
            <div class="hero-trust-row">
              <span class="trust-chip"><svg class="check-icon" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/></svg>{{ t.hero.trust1 }}</span>
              <span class="trust-chip"><svg class="check-icon" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/></svg>{{ t.hero.trust2 }}</span>
              <span class="trust-chip"><svg class="check-icon" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/></svg>{{ t.hero.trust3 }}</span>
            </div>
          </div>
          <div class="hero-visual" data-aos="fade-left">
            <div class="hero-img-main">
              <img src="https://static.wixstatic.com/media/575a2a_67257af414b14ce4a7941062339a4bc2f000.jpg/v1/fill/w_800,h_500,al_c,q_85,enc_avif,quality_auto/575a2a_67257af414b14ce4a7941062339a4bc2f000.jpg" alt="Warehouse Operations" />
              <div class="hero-img-badge">
                <span class="hero-img-badge-dot"></span>
                {{ t.home.operationsLive }}
              </div>
            </div>
            <div class="hero-float-card hero-float-card--1">
              <div class="float-icon float-icon--green">
                <i class="fa-solid fa-chart-line"></i>
              </div>
              <div>
                <div class="float-num">98%</div>
                <div class="float-label">{{ t.home.dispatch24h }}</div>
              </div>
            </div>
            <div class="hero-float-card hero-float-card--2">
              <div class="float-icon float-icon--blue">
                <i class="fa-solid fa-boxes-stacked"></i>
              </div>
              <div>
                <div class="float-num">670K+</div>
                <div class="float-label">{{ t.home.skuAvailable }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Trust badges row -->
      <div class="hero-trust-bar">
        <div class="hero-trust-bar-inner">
          <div class="trust-badge-item">
            <img src="https://static.wixstatic.com/media/575a2a_14df05badf414fcc964b6593090700c0~mv2.png/v1/fill/w_140,h_28,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/FFOrder-trustpilot.png" alt="Trustpilot" />
          </div>
          <div class="trust-badge-item">
            <img src="https://static.wixstatic.com/media/575a2a_7ac3c7f0e9ca4381923f4d6d9e623888~mv2.png/v1/fill/w_140,h_28,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/FFOrder-app-store.png" alt="Shopify App Store" />
          </div>
          <div class="trust-badge-divider"></div>
          <span class="trust-badge-label">{{ t.home.integratedWith }}</span>
          <div class="trust-badge-platforms">
            <span class="tb-platform"><i class="fa-brands fa-shopify"></i> Shopify</span>
            <span class="tb-platform"><i class="fa-brands fa-wordpress"></i> WooCommerce</span>
            <span class="tb-platform"><i class="fa-brands fa-amazon"></i> Amazon</span>
            <span class="tb-platform"><i class="fa-brands fa-ebay"></i> eBay</span>
            <span class="tb-platform"><i class="fa-brands fa-tiktok"></i> TikTok Shop</span>
          </div>
        </div>
      </div>
    </section>

    <!-- Stats Bar Section -->
    <section class="stats-bar">
      <div class="stats-bar-inner">
        <div class="stat-item" v-for="(stat, i) in statsItems" :key="i">
          <div class="stat-big-num">{{ stat.num }}</div>
          <div class="stat-big-label">{{ stat.label }}</div>
        </div>
      </div>
      <p class="stats-bar-quote">{{ t.home.statsQuote }}</p>
    </section>

    <!-- Why Trust Us Section (with real photos) -->
    <section class="trust-section">
      <div class="section-inner">
        <div class="section-header" data-aos="fade-up">
          <span class="section-eyebrow">{{ t.home.whyChoose }}</span>
          <h2 class="section-title">{{ t.home.whySubtitle }}</h2>
          <p class="section-desc">{{ t.home.whyDesc }}</p>
        </div>
        <div class="trust-grid">
          <div class="trust-card" data-aos="fade-up" data-aos-delay="0">
            <div class="trust-card-img">
              <img src="https://static.wixstatic.com/media/575a2a_099a0e8719fe4183811d50c5c3bae7c1~mv2.jpg/v1/fill/w_600,h_340,al_c,q_80,enc_avif,quality_auto/1773911073629_edited.jpg" :alt="t.home.why1Title" />
            </div>
            <div class="trust-card-body">
              <div class="trust-card-icon">
                <i class="fa-solid fa-shield-halved"></i>
              </div>
              <h3>{{ t.home.why1Title }}</h3>
              <p>{{ t.home.why1Desc }}</p>
            </div>
          </div>
          <div class="trust-card" data-aos="fade-up" data-aos-delay="100">
            <div class="trust-card-img">
              <img src="https://static.wixstatic.com/media/575a2a_30b96448cffb4c349febe01c7efc6a15~mv2.jpg/v1/fill/w_600,h_340,al_c,q_80,enc_avif,quality_auto/Cross-Border%20Operations%20Team_edited.jpg" alt="Synchronized Communication" />
            </div>
            <div class="trust-card-body">
              <div class="trust-card-icon">
                <i class="fa-solid fa-comments"></i>
              </div>
              <h3>{{ t.home.why2Title }}</h3>
              <p>{{ t.home.why2Desc }}</p>
            </div>
          </div>
          <div class="trust-card" data-aos="fade-up" data-aos-delay="200">
            <div class="trust-card-img">
              <img src="https://static.wixstatic.com/media/575a2a_9885ae085e2e4644b09a9d19b2231e0e~mv2.jpg/v1/fill/w_600,h_340,al_c,q_80,enc_avif,quality_auto/Custom%20Packaging%20Operations_edited.jpg" alt="Custom Flexibility" />
            </div>
            <div class="trust-card-body">
              <div class="trust-card-icon">
                <i class="fa-solid fa-sliders"></i>
              </div>
              <h3>{{ t.home.why3Title }}</h3>
              <p>{{ t.home.why3Desc }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Capabilities Section -->
    <section class="capabilities">
      <div class="section-inner">
        <div class="section-header" data-aos="fade-up">
          <span class="section-eyebrow">Core Capabilities</span>
          <h2 class="section-title">{{ t.capabilities.title }}</h2>
          <p class="section-desc">Everything you need to run a stable, scalable cross-border supply chain — in one place.</p>
        </div>
        <div class="capabilities-grid">
          <div
            v-for="(item, index) in t.capabilities.items"
            :key="index"
            class="cap-card"
            data-aos="fade-up"
            :data-aos-delay="index * 60"
          >
            <div class="cap-card-top">
              <div class="cap-icon-wrap" :class="'cap-icon-wrap--' + (index % 6)">
                <i :class="['fa-solid', 'fa-' + getCapabilityIcon(index)]"></i>
              </div>
              <span class="cap-num">{{ item.number }}</span>
            </div>
            <h3 class="cap-title">{{ item.title }}</h3>
            <p class="cap-desc">{{ item.description }}</p>
            <a class="cap-link" @click.prevent="scrollToContact" href="#">Learn more →</a>
          </div>
        </div>
      </div>
    </section>

    <!-- How It Works Section -->
    <section class="how-it-works">
      <div class="section-inner">
        <div class="section-header" data-aos="fade-up">
          <span class="section-eyebrow">How It Works</span>
          <h2 class="section-title">{{ t.supplyChain.title }}</h2>
          <p class="section-desc">{{ t.supplyChain.subtitle }}</p>
        </div>
        <div class="how-steps">
          <div
            v-for="(item, index) in t.supplyChain.items"
            :key="index"
            class="how-step"
            data-aos="fade-up"
            :data-aos-delay="index * 80"
          >
            <div class="step-num-wrap">
              <span class="step-num">{{ String(index + 1).padStart(2, '0') }}</span>
              <div class="step-line" v-if="index < t.supplyChain.items.length - 1"></div>
            </div>
            <div class="step-body">
              <div class="step-icon-wrap">
                <i :class="['fa-solid', 'fa-' + getSupplyChainIcon(index)]"></i>
              </div>
              <h3 class="step-title">{{ item.title }}</h3>
              <p class="step-desc">{{ item.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Solutions / Business Models Section (with real images) -->
    <section class="solutions-section">
      <div class="section-inner">
        <div class="section-header" data-aos="fade-up">
          <span class="section-eyebrow">Solutions</span>
          <h2 class="section-title">{{ t.businessModels.title }}</h2>
          <p class="section-desc">{{ t.businessModels.subtitle }}</p>
        </div>
        <div class="solutions-grid">
          <div class="sol-card" data-aos="fade-up" data-aos-delay="0">
            <div class="sol-card-img">
              <img src="https://static.wixstatic.com/media/575a2a_a46e4c3252d346789313e3480eae0081~mv2.png/v1/fill/w_300,h_200,al_c,q_85,enc_avif,quality_auto/575a2a_a46e4c3252d346789313e3480eae0081~mv2.png" alt="Dropshipping" />
            </div>
            <div class="sol-card-body">
              <span class="sol-badge">For Dropshippers</span>
              <h3 class="sol-title">Dropshipping Fulfillment</h3>
              <p class="sol-desc">Sell without inventory risk. We source, store, and ship products directly to your customers worldwide with fast delivery times.</p>
              <a class="sol-cta" href="#" @click.prevent="scrollToContact">Get Started <span>→</span></a>
            </div>
          </div>
          <div class="sol-card" data-aos="fade-up" data-aos-delay="100">
            <div class="sol-card-img">
              <img src="https://static.wixstatic.com/media/575a2a_812d252af6ea443aba66d66751eae5f0~mv2.png/v1/fill/w_300,h_200,al_c,q_85,enc_avif,quality_auto/575a2a_812d252af6ea443aba66d66751eae5f0~mv2.png" alt="DTC Brands" />
            </div>
            <div class="sol-card-body">
              <span class="sol-badge sol-badge--blue">For DTC Brands</span>
              <h3 class="sol-title">Brand Fulfillment</h3>
              <p class="sol-desc">Build your brand with custom packaging, branded inserts, and white-label shipping. Scale from 100 to 100,000 orders seamlessly.</p>
              <a class="sol-cta" href="#" @click.prevent="scrollToContact">Get Started <span>→</span></a>
            </div>
          </div>
          <div class="sol-card" data-aos="fade-up" data-aos-delay="200">
            <div class="sol-card-img">
              <img src="https://static.wixstatic.com/media/575a2a_9661c470e6c64d31bc14fb2946703ea2~mv2.png/v1/fill/w_300,h_200,al_c,q_85,enc_avif,quality_auto/575a2a_9661c470e6c64d31bc14fb2946703ea2~mv2.png" alt="Ecommerce Fulfillment" />
            </div>
            <div class="sol-card-body">
              <span class="sol-badge sol-badge--green">For Ecommerce</span>
              <h3 class="sol-title">Enterprise Fulfillment</h3>
              <p class="sol-desc">Multi-warehouse, multi-platform fulfillment for high-volume sellers. API-first integration with real-time inventory sync.</p>
              <a class="sol-cta" href="#" @click.prevent="scrollToContact">Get Started <span>→</span></a>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Industry Expertise Section (with real images) -->
    <section class="industry-section">
      <div class="section-inner">
        <div class="section-header" data-aos="fade-up">
          <span class="section-eyebrow">{{ t.home.industryTitle }}</span>
          <h2 class="section-title">{{ t.home.industryTitle }}</h2>
          <p class="section-desc">{{ t.home.industrySubtitle }}</p>
        </div>
        <div class="industry-grid">
          <div class="industry-card" data-aos="fade-up" data-aos-delay="0">
            <div class="industry-card-img">
              <img src="https://static.wixstatic.com/media/575a2a_c27a7b2d3f9847678568c50ea6d06810~mv2.png/v1/fill/w_160,h_160,al_c,q_85,enc_avif,quality_auto/2.png" alt="Fashion & Apparel" />
              <div class="industry-overlay">
                <span class="industry-tag">Fashion & Apparel</span>
              </div>
            </div>
            <div class="industry-card-body">
              <h4>{{ t.home.industry1Title }}</h4>
              <p>{{ t.home.industry1Desc }}</p>
              <div class="industry-tags">
                <span>Size QC</span><span>Fabric Check</span><span>Custom Labels</span>
              </div>
            </div>
          </div>
          <div class="industry-card" data-aos="fade-up" data-aos-delay="100">
            <div class="industry-card-img">
              <img src="https://static.wixstatic.com/media/575a2a_b0c624181227498085cedc6fbfe10638~mv2.png/v1/fill/w_160,h_110,al_c,q_85,enc_avif,quality_auto/%E8%A1%A5%E5%89%82.png" alt="Health Supplements" />
              <div class="industry-overlay">
                <span class="industry-tag">Health Supplements</span>
              </div>
            </div>
            <div class="industry-card-body">
              <h4>{{ t.home.industry2Title }}</h4>
              <p>{{ t.home.industry2Desc }}</p>
              <div class="industry-tags">
                <span>Batch Tracking</span><span>Compliance</span><span>Certified Storage</span>
              </div>
            </div>
          </div>
          <div class="industry-card" data-aos="fade-up" data-aos-delay="200">
            <div class="industry-card-img">
              <img src="https://static.wixstatic.com/media/575a2a_f2ac3c53f08d4322a3a7dc238155d975~mv2.png/v1/fill/w_160,h_110,al_c,q_85,enc_avif,quality_auto/ChatGPT%20Image%202026%E5%B9%B41%E6%9C%8830%E6%97%A5%2017_05_41.png" alt="Lighting Products" />
              <div class="industry-overlay">
                <span class="industry-tag">Lighting & Home</span>
              </div>
            </div>
            <div class="industry-card-body">
              <h4>{{ t.home.industry3Title }}</h4>
              <p>{{ t.home.industry3Desc }}</p>
              <div class="industry-tags">
                <span>Fragile Handling</span><span>Insurance</span><span>Protective Packaging</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Operating System Section (feature highlight) -->
    <section class="os-section">
      <div class="section-inner">
        <div class="section-header" data-aos="fade-up">
          <span class="section-eyebrow">Platform</span>
          <h2 class="section-title">{{ t.operatingSystem.title }}</h2>
          <p class="section-desc">{{ t.operatingSystem.subtitle }}</p>
        </div>
        <div class="os-showcase" data-aos="fade-up">
          <!-- Dashboard Mockup -->
          <div class="os-dashboard-mockup">
            <div class="dash-sidebar">
              <div class="dash-logo">
                <img src="@/assets/logo-icon-white.png" alt="" width="24" height="28" />
              </div>
              <div class="dash-nav">
                <div class="dash-nav-item active"><i class="fa-solid fa-chart-pie"></i> Dashboard</div>
                <div class="dash-nav-item"><i class="fa-solid fa-box"></i> Orders</div>
                <div class="dash-nav-item"><i class="fa-solid fa-warehouse"></i> Inventory</div>
                <div class="dash-nav-item"><i class="fa-solid fa-truck"></i> Shipping</div>
                <div class="dash-nav-item"><i class="fa-solid fa-shield-halved"></i> QC</div>
                <div class="dash-nav-item"><i class="fa-solid fa-headset"></i> Support</div>
              </div>
            </div>
            <div class="dash-main">
              <div class="dash-topbar">
                <div class="dash-breadcrumb">Dashboard <span>/ Overview</span></div>
                <div class="dash-topbar-right">
                  <div class="dash-search"><i class="fa-solid fa-magnifying-glass"></i></div>
                  <div class="dash-notif"><i class="fa-solid fa-bell"></i><span class="notif-dot"></span></div>
                  <div class="dash-avatar">W</div>
                </div>
              </div>
              <div class="dash-stats-row">
                <div class="dash-stat-card" v-for="(s, si) in dashStats" :key="si" :class="'dash-stat--' + si">
                  <div class="dash-stat-icon"><i :class="s.icon"></i></div>
                  <div>
                    <div class="dash-stat-value">{{ s.value }}</div>
                    <div class="dash-stat-label">{{ s.label }}</div>
                  </div>
                  <div class="dash-stat-trend" :class="s.trendUp ? 'up' : 'down'">{{ s.trend }}</div>
                </div>
              </div>
              <div class="dash-content-row">
                <div class="dash-chart-panel">
                  <div class="dash-panel-header">
                    <span>Order Volume (30d)</span>
                    <span class="dash-panel-badge">Live</span>
                  </div>
                  <div class="dash-chart-area">
                    <svg viewBox="0 0 300 80" class="dash-chart-svg">
                      <defs>
                        <linearGradient id="chartGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stop-color="#818cf8" stop-opacity="0.3"/>
                          <stop offset="100%" stop-color="#818cf8" stop-opacity="0"/>
                        </linearGradient>
                      </defs>
                      <path d="M0,60 Q20,55 40,48 T80,35 T120,40 T160,25 T200,20 T240,15 T280,10 T300,8" fill="none" stroke="#818cf8" stroke-width="2.5"/>
                      <path d="M0,60 Q20,55 40,48 T80,35 T120,40 T160,25 T200,20 T240,15 T280,10 T300,8 V80 H0 Z" fill="url(#chartGrad)"/>
                    </svg>
                  </div>
                </div>
                <div class="dash-status-panel">
                  <div class="dash-panel-header">
                    <span>Order Status</span>
                  </div>
                  <div class="dash-status-list">
                    <div class="dash-status-item" v-for="(os, oi) in dashOrderStatus" :key="oi">
                      <div class="dash-status-dot" :style="{ background: os.color }"></div>
                      <span class="dash-status-label">{{ os.label }}</span>
                      <span class="dash-status-pct">{{ os.pct }}</span>
                      <div class="dash-status-bar-bg"><div class="dash-status-bar-fill" :style="{ width: os.pct, background: os.color }"></div></div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="dash-recent-row">
                <div class="dash-recent-panel">
                  <div class="dash-panel-header"><span>Recent Orders</span></div>
                  <div class="dash-recent-list">
                    <div class="dash-order-item" v-for="(ord, oi2) in dashRecentOrders" :key="oi2">
                      <div class="dash-order-id">{{ ord.id }}</div>
                      <div class="dash-order-sku">{{ ord.sku }}</div>
                      <div class="dash-order-status" :class="'status-' + ord.statusClass">{{ ord.status }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="os-showcase-features">
            <div class="os-feature-item" v-for="(item, index) in t.operatingSystem.items" :key="index">
              <div class="os-feature-icon" :class="'os-feature-icon--' + (index % 6)">
                <i :class="['fa-solid', 'fa-' + getOperatingSystemIcon(index)]"></i>
              </div>
              <div class="os-feature-content">
                <h4>{{ item.title }}</h4>
                <p>{{ item.description }}</p>
                <div class="os-highlight-row">
                  <span class="os-highlight" v-for="(tag, ti) in item.highlight.split('·')" :key="ti">{{ tag.trim() }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Case Study Section (with real image) -->
    <section class="case-section">
      <div class="section-inner">
        <div class="section-header" data-aos="fade-up">
          <span class="section-eyebrow">{{ t.caseStudy.title }}</span>
          <h2 class="section-title">{{ t.caseStudy.subtitle }}</h2>
        </div>
        <div class="case-card" data-aos="fade-up">
          <div class="case-card-left">
            <span class="case-badge">{{ t.caseStudy.badge }}</span>
            <h3 class="case-title">{{ t.caseStudy.caseTitle }}</h3>
            <p class="case-intro">{{ t.caseStudy.intro }}</p>
            <h5 class="case-factors-title">{{ t.caseStudy.factors.title }}</h5>
            <ul class="case-factors-list">
              <li v-for="(f, fi) in t.caseStudy.factors.items" :key="fi">
                <svg class="check-icon-green" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/></svg>
                {{ f }}
              </li>
            </ul>
            <button class="btn-primary" @click="scrollToContact">{{ t.caseStudy.btn }}</button>
          </div>
          <div class="case-card-right">
            <div class="case-img-wrap">
              <img src="https://static.wixstatic.com/media/575a2a_83df7795f2f64b4db8fd052e30d61870~mv2.png/v1/fill/w_280,h_280,al_c,q_85,enc_avif,quality_auto/3.png" alt="Case Study" />
            </div>
            <h5 class="case-results-title">{{ t.caseStudy.results.title }}</h5>
            <div class="case-results-grid">
              <div class="case-result" v-for="(r, ri) in t.caseStudy.results.items" :key="ri">
                <div class="case-result-num">{{ r.number }}</div>
                <div class="case-result-label">{{ r.label }}</div>
              </div>
            </div>
            <div class="case-testimonial">
              <p class="case-quote">"FULFILLDROP helped us go from chaos to system. Order accuracy is now above 99% and our team can focus on growth."</p>
              <div class="case-author">
                <div class="case-author-avatar">W</div>
                <div>
                  <div class="case-author-name">Warren Chen</div>
                  <div class="case-author-role">Founder, Home Goods Store</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Integration Carousel (with real images) -->
    <section class="integrations-section">
      <div class="section-inner">
        <div class="section-header" data-aos="fade-up">
          <span class="section-eyebrow">Integrations</span>
          <h2 class="section-title">Connect your store in minutes</h2>
          <p class="section-desc">Seamlessly sync orders, inventory, and tracking across all major ecommerce platforms and marketplaces.</p>
        </div>
        <div class="integrations-carousel" data-aos="fade-up">
          <div class="carousel-track">
            <div class="carousel-item" v-for="(img, i) in integrationImages" :key="'a'+i">
              <img :src="img" alt="Platform Integration" />
            </div>
            <div class="carousel-item" v-for="(img, i) in integrationImages" :key="'b'+i">
              <img :src="img" alt="Platform Integration" />
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Testimonials Section -->
    <section class="testimonials-section">
      <div class="section-inner">
        <div class="section-header" data-aos="fade-up">
          <span class="section-eyebrow">Testimonials</span>
          <h2 class="section-title">What our sellers say</h2>
        </div>
        <div class="testimonials-grid" data-aos="fade-up">
          <div class="testimonial-card" v-for="(t, i) in testimonials" :key="i">
            <div class="testimonial-stars">
              <i class="fa-solid fa-star" v-for="n in 5" :key="n"></i>
            </div>
            <p class="testimonial-text">{{ t.text }}</p>
            <div class="testimonial-author">
              <div class="testimonial-avatar">{{ t.name[0] }}</div>
              <div>
                <div class="testimonial-name">{{ t.name }}</div>
                <div class="testimonial-role">{{ t.role }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Partners Section -->
    <section class="partners-section">
      <div class="section-inner">
        <div class="section-header" data-aos="fade-up">
          <span class="section-eyebrow">{{ t.partners.title }}</span>
          <h2 class="section-title" style="font-size:1.8rem;">{{ t.partners.subtitle }}</h2>
        </div>
        <div class="partners-track-wrap">
          <div class="partners-track">
            <div class="partner-item" v-for="(partner, index) in [...partners, ...partners]" :key="'p'+index">
              <div class="partner-icon-wrap">
                <i :class="['fa-brands', 'fa-' + partner.icon]"></i>
              </div>
              <span>{{ partner.name }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="cta-section">
      <div class="cta-inner" data-aos="fade-up">
        <div class="cta-decoration">
          <img src="https://static.wixstatic.com/media/575a2a_ce49b59c96fc435ca7eb2edcc8d1f94f~mv2.png/v1/fill/w_120,h_60,al_c,q_85,enc_avif,quality_auto/%E8%B5%84%E6%BA%90%206-8.png" alt="" />
        </div>
        <h2 class="cta-title">Build a more stable backend<br>before growth exposes the cracks</h2>
        <p class="cta-sub">No subscription fees. No MOQs. Get started anytime.</p>
        <div class="cta-btn-group">
          <button class="btn-primary btn-primary--lg" @click="scrollToContact">{{ t.hero.btn }}</button>
          <button class="btn-ghost btn-ghost--lg" @click="scrollToContact">{{ t.home.ctaSecondary }}</button>
        </div>
      </div>
    </section>

    <!-- Contact Section -->
    <section class="contact" id="contact">
      <div class="section-inner">
        <div class="section-header" data-aos="fade-up">
          <span class="section-eyebrow">Contact</span>
          <h2 class="section-title">{{ t.contact.title }}</h2>
          <p class="section-desc">{{ t.contact.subtitle }}</p>
        </div>
        <div class="contact-layout">
          <div class="contact-form-box" data-aos="fade-right">
            <h4 class="contact-form-title">{{ t.contact.form.title }}</h4>
            <p class="contact-form-text">{{ t.contact.form.text2 }}</p>
            <form class="form" @submit.prevent="handleContactSubmit">
              <div class="form-row">
                <div class="form-group">
                  <label>{{ t.contact.form.name }}</label>
                  <input type="text" v-model.trim="contactForm.name" :placeholder="t.contact.form.placeholder.name" required>
                </div>
                <div class="form-group">
                  <label>{{ t.contact.form.email }}</label>
                  <input type="email" v-model.trim="contactForm.email" :placeholder="t.contact.form.placeholder.email" required>
                </div>
              </div>
              <div class="form-group">
                <label>{{ t.contact.form.phone }}</label>
                <input type="tel" v-model.trim="contactForm.phone" :placeholder="t.contact.form.placeholder.phone" required>
              </div>
              <div class="form-group">
                <label>{{ t.contact.form.message }}</label>
                <textarea v-model.trim="contactForm.message" :placeholder="t.contact.form.placeholder.message" required></textarea>
              </div>
              <button type="submit" class="btn-primary" style="width:100%">{{ t.contact.form.btn }}</button>
            </form>
          </div>
          <div class="contact-info-box" data-aos="fade-left">
            <h4 class="contact-info-title">{{ t.contact.info.title }}</h4>
            <ul class="contact-info-list">
              <li v-for="(item, index) in t.contact.info.items" :key="index">
                <div class="contact-info-icon">
                  <i :class="['fa-solid', 'fa-' + item.icon]"></i>
                </div>
                <span>{{ item.text }}</span>
              </li>
            </ul>
            <div class="contact-features">
              <div class="contact-feature" v-for="(f, fi) in contactFeatures" :key="fi">
                <svg class="check-icon-green" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/></svg>
                {{ f }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Footer -->
    <Footer :currentLang="currentLang" />
  </div>
</template>

<script>
import Navbar from '../components/Navbar.vue'
import Footer from '../components/Footer.vue'
import { getSafeTranslator } from '../assets/languages.js'

export default {
  name: 'Home',
  props: {
    currentLang: { type: String, default: 'en' }
  },
  components: { Navbar, Footer },
  data() {
    return {
      contactForm: { name: '', email: '', phone: '', message: '' },
      statsItems: [
        { num: '670K+', label: 'SKUs Available to Source' },
        { num: '50K', label: '{{ t.home.statsOrders }} Daily' },
        { num: '100+', label: 'Shipping Routes Worldwide' },
        { num: '99.8%', label: 'Successful Delivery Rate' },
        { num: '5–12', label: 'Days Global Delivery' },
      ],
      contactFeatures: [
        'Reply within 24 hours',
        'No subscription fee required',
        'Free supply chain consultation',
        'Dedicated account manager'
      ],
      partners: [
        { name: 'Shopify', icon: 'shopify' },
        { name: 'WooCommerce', icon: 'wordpress' },
        { name: 'Amazon', icon: 'amazon' },
        { name: 'eBay', icon: 'ebay' },
        { name: 'TikTok Shop', icon: 'tiktok' },
        { name: 'Etsy', icon: 'etsy' },
        { name: 'FedEx', icon: 'fedex' },
        { name: 'PayPal', icon: 'paypal' },
      ],
      integrationImages: [
        'https://static.wixstatic.com/media/575a2a_3f47341d16ca4f4b9e2f8ed488e08070~mv2.jpg/v1/fit/w_480,h_480,q_90,enc_avif,quality_auto/575a2a_3f47341d16ca4f4b9e2f8ed488e08070~mv2.jpg',
        'https://static.wixstatic.com/media/575a2a_86bb72572ccc4412b795107a130ee0e2~mv2.jpg/v1/fit/w_480,h_480,q_90,enc_avif,quality_auto/575a2a_86bb72572ccc4412b795107a130ee0e2~mv2.jpg',
        'https://static.wixstatic.com/media/575a2a_693bdababfcb4ea88dabff857ec15372~mv2.jpg/v1/fit/w_480,h_480,q_90,enc_avif,quality_auto/575a2a_693bdababfcb4ea88dabff857ec15372~mv2.jpg',
        'https://static.wixstatic.com/media/575a2a_4587e1814df749dcbf7c7aab7174f745~mv2.jpg/v1/fit/w_480,h_480,q_90,enc_avif,quality_auto/575a2a_4587e1814df749dcbf7c7aab7174f745~mv2.jpg',
      ],
      testimonials: [
        { text: '"FULFILLDROP has been a game-changer for our dropshipping business. Their proactive communication and fast shipping times have helped us scale to 500+ orders per day."', name: 'Sarah M.', role: 'Dropshipping Entrepreneur' },
        { text: '"We switched from another fulfillment partner and the difference is night and day. Order accuracy went from 92% to 99.5% within the first month."', name: 'David L.', role: 'DTC Brand Owner' },
        { text: '"The custom packaging options and branded inserts have really elevated our brand presence. Our customers always mention the professional unboxing experience."', name: 'Emma R.', role: 'E-commerce Director' },
      ],
      dashStats: [
        { icon: 'fa-solid fa-cart-shopping', value: '1,847', label: 'Orders Today', trend: '+12.3%', trendUp: true },
        { icon: 'fa-solid fa-boxes-stacked', value: '23,591', label: 'SKUs in Stock', trend: '+3.8%', trendUp: true },
        { icon: 'fa-solid fa-truck-fast', value: '98.5%', label: 'On-Time Dispatch', trend: '+0.6%', trendUp: true },
        { icon: 'fa-solid fa-rotate-left', value: '1.2%', label: 'Return Rate', trend: '-0.3%', trendUp: true },
      ],
      dashOrderStatus: [
        { label: 'Delivered', pct: '68%', color: '#22c55e' },
        { label: 'In Transit', pct: '22%', color: '#3b82f6' },
        { label: 'Processing', pct: '8%', color: '#f59e0b' },
        { label: 'Exception', pct: '2%', color: '#ef4444' },
      ],
      dashRecentOrders: [
        { id: 'FD-28471', sku: 'BLU-MUG-01 ×2', status: 'Shipped', statusClass: 'shipped' },
        { id: 'FD-28470', sku: 'HML-LAMP-03 ×1', status: 'Packing', statusClass: 'packing' },
        { id: 'FD-28469', sku: 'FAS-TSHRT-12 ×3', status: 'QC Check', statusClass: 'qc' },
        { id: 'FD-28468', sku: 'HLT-VITMN-05 ×1', status: 'Delivered', statusClass: 'delivered' },
      ]
    }
  },
  computed: {
    t() { return getSafeTranslator(this.currentLang) }
  },
  methods: {
    changeLang(lang) { this.$emit('change-lang', lang) },
    getCapabilityIcon(index) {
      return ['store', 'handshake-angle', 'check-circle', 'boxes-stacked', 'truck-fast', 'headset'][index] || 'star'
    },
    getSupplyChainIcon(index) {
      return ['route', 'arrows-rotate', 'pencil', 'circle-check', 'eye'][index] || 'star'
    },
    getOperatingSystemIcon(index) {
      return ['truck-fast', 'palette', 'warehouse', 'truck', 'shield-check', 'headset'][index] || 'star'
    },
    handleContactSubmit() {
      const sendkey = 'SCT342335TIsFNci9EpNmO1RHEbA2amisa'
      const notifyTitle = `FULFILLDROP Website Inquiry - ${this.contactForm.name || 'Visitor'}`
      const notifyDesp = `**Name:** ${this.contactForm.name}\n**Email:** ${this.contactForm.email}\n**Phone:** ${this.contactForm.phone}\n\n**Message:**\n${this.contactForm.message}`
      fetch(`https://sctapi.ftqq.com/${sendkey}.send?title=${encodeURIComponent(notifyTitle)}&desp=${encodeURIComponent(notifyDesp)}`)
        .then(r => r.json())
        .then(() => {
          alert('Submitted successfully! We will contact you within 24 hours.')
          this.contactForm = { name: '', email: '', phone: '', message: '' }
        })
        .catch(() => alert('Submission failed, please try again later.'))
    },
    scrollToContact() {
      const el = document.getElementById('contact')
      if (el) el.scrollIntoView({ behavior: 'smooth' })
    }
  }
}
</script>

<style scoped>
/* ============================================================
   GLOBAL
   ============================================================ */
.home {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  color: #0f172a;
  background: #fff;
  overflow-x: hidden;
}
.section-inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
}
.section-header {
  text-align: center;
  margin-bottom: 5rem;
}
.section-eyebrow {
  display: inline-block;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #28b5c1;
  background: linear-gradient(135deg, rgba(40, 181, 193, 0.1) 0%, rgba(40, 181, 193, 0.06) 100%);
  padding: 0.45rem 1.25rem;
  border-radius: 999px;
  margin-bottom: 1.25rem;
  border: 1px solid rgba(40, 181, 193, 0.12);
  transition: all 0.3s ease;
}
.section-eyebrow:hover {
  background: linear-gradient(135deg, rgba(40, 181, 193, 0.15) 0%, rgba(40, 181, 193, 0.1) 100%);
  transform: translateY(-1px);
}
.section-title {
  font-size: clamp(2.2rem, 4vw, 3rem);
  font-weight: 800;
  color: #0f172a;
  line-height: 1.12;
  margin-bottom: 1.25rem;
  letter-spacing: -0.02em;
}
.section-desc {
  font-size: 1.15rem;
  color: #64748b;
  max-width: 720px;
  margin: 0 auto;
  line-height: 1.8;
}

/* Buttons */
.btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: #28b5c1;
  color: #fff;
  border: none;
  padding: 0.9rem 2rem;
  border-radius: 10px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: background 0.2s, transform 0.2s, box-shadow 0.2s;
  box-shadow: 0 4px 16px rgba(40, 181, 193, 0.28);
}
.btn-primary:hover {
  background: #1e9aa5;
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(40, 181, 193, 0.36);
}
.btn-primary--lg { padding: 1.1rem 2.5rem; font-size: 1.1rem; }
.btn-arrow { width: 18px; height: 18px; }
.btn-ghost {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: transparent;
  color: #0f172a;
  border: 1.5px solid #cbd5e1;
  padding: 0.9rem 2rem;
  border-radius: 10px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
}
.btn-ghost:hover { border-color: #28b5c1; color: #28b5c1; background: rgba(40,181,193,0.04); }
.btn-ghost--lg { padding: 1.1rem 2.5rem; font-size: 1.1rem; }
.whatsapp-icon { width: 18px; height: 18px; }

/* ============================================================
   HERO
   ============================================================ */
.hero {
  background: linear-gradient(160deg, #0f172a 0%, #1e1b4b 50%, #312e81 100%);
  padding-top: 5rem;
  padding-bottom: 0;
  position: relative;
  overflow: hidden;
}
.hero-bg-overlay {
  position: absolute;
  inset: 0;
  background: url('https://static.wixstatic.com/media/575a2a_67257af414b14ce4a7941062339a4bc2f000.jpg/v1/fill/w_1920,h_1080,al_c,q_60,enc_avif,quality_auto/575a2a_67257af414b14ce4a7941062339a4bc2f000.jpg') center/cover;
  opacity: 0.15;
  pointer-events: none;
}
.hero-inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 1;
}
.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255,255,255,0.1);
  border: 1.5px solid rgba(255,255,255,0.15);
  border-radius: 999px;
  padding: 0.45rem 1.1rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: #a5b4fc;
  margin-bottom: 2.5rem;
  backdrop-filter: blur(8px);
}
.badge-dot {
  width: 8px; height: 8px;
  background: #22c55e;
  border-radius: 50%;
  animation: badgePulse 1.8s infinite;
}
@keyframes badgePulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(34,197,94,0.4); }
  50% { box-shadow: 0 0 0 6px rgba(34,197,94,0); }
}
.hero-body {
  display: flex;
  align-items: center;
  gap: 4rem;
  padding-bottom: 4rem;
}
.hero-content { flex: 1; min-width: 0; }
.hero-eyebrow {
  display: block;
  font-size: 0.9rem;
  font-weight: 600;
  color: #a5b4fc;
  margin-bottom: 0.6rem;
  letter-spacing: 0.02em;
}
.hero-title-2 {
  display: block;
  font-size: clamp(2.4rem, 4vw, 3.6rem);
  font-weight: 900;
  line-height: 1.1;
  color: #fff;
  margin-bottom: 1.5rem;
}
.hero-description {
  font-size: 1.15rem;
  color: #94a3b8;
  line-height: 1.7;
  margin-bottom: 2.2rem;
  max-width: 520px;
}
.hero-cta-group {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.8rem;
}
.hero .btn-primary {
  background: linear-gradient(135deg, #ffffff 0%, #f0f4ff 100%);
  color: #28b5c1;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.22);
  font-weight: 700;
}
.hero .btn-primary:hover { 
  background: linear-gradient(135deg, #f8fafc 0%, #e0e7ff 100%); 
  transform: translateY(-3px); 
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.28);
}
.hero .btn-ghost { 
  border-color: rgba(255, 255, 255, 0.4); 
  color: #fff; 
  background: rgba(255, 255, 255, 0.06);
  backdrop-filter: blur(8px);
}
.hero .btn-ghost:hover { 
  background: rgba(255, 255, 255, 0.15); 
  border-color: rgba(255, 255, 255, 0.6); 
  color: #fff; 
  transform: translateY(-2px);
}
.hero-trust-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
}
.trust-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: #e2e8f0;
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 999px;
  padding: 0.45rem 1rem;
  transition: all 0.3s ease;
}
.trust-chip:hover {
  background: rgba(255, 255, 255, 0.18);
  transform: translateY(-1px);
}
.hero .check-icon { color: #22c55e; }
.check-icon-green { width: 16px; height: 16px; color: #22c55e; flex-shrink: 0; }

/* Hero Visual */
.hero-visual { flex: 0 0 520px; position: relative; }
.hero-img-main {
  width: 100%;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 32px 64px rgba(0,0,0,0.4);
  position: relative;
}
.hero-img-main img {
  width: 100%;
  height: auto;
  display: block;
  aspect-ratio: 16/10;
  object-fit: cover;
}
.hero-img-badge {
  position: absolute;
  bottom: 14px; left: 14px;
  background: rgba(0,0,0,0.55);
  backdrop-filter: blur(8px);
  color: #fff;
  font-size: 0.78rem;
  font-weight: 600;
  padding: 0.4rem 0.85rem;
  border-radius: 999px;
  display: flex; align-items: center; gap: 0.4rem;
}
.hero-img-badge-dot { width: 7px; height: 7px; background: #22c55e; border-radius: 50%; animation: badgePulse 1.8s infinite; }

/* Float cards */
.hero-float-card {
  position: absolute;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(241, 245, 249, 0.98) 100%);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  padding: 1rem 1.3rem;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.18);
  display: flex;
  align-items: center;
  gap: 0.9rem;
  min-width: 190px;
  border: 1px solid rgba(255, 255, 255, 0.5);
  animation: floatCard 3.5s ease-in-out infinite;
  transition: all 0.3s ease;
}
.hero-float-card:hover {
  transform: translateY(-4px) scale(1.02);
  box-shadow: 0 20px 48px rgba(0, 0, 0, 0.22);
}
.hero-float-card--1 { top: -20px; right: -25px; animation-delay: 0s; }
.hero-float-card--2 { bottom: 30px; left: -30px; animation-delay: 1.5s; }
@keyframes floatCard {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
}
.float-icon {
  width: 40px; height: 40px;
  border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  font-size: 1.1rem;
}
.float-icon--green { background: #dcfce7; color: #16a34a; }
.float-icon--blue { background: #dbeafe; color: #2563eb; }
.float-num { font-size: 1.2rem; font-weight: 800; color: #0f172a; line-height: 1; }
.float-label { font-size: 0.72rem; color: #64748b; margin-top: 0.15rem; }

/* Hero Trust Bar */
.hero-trust-bar {
  background: rgba(255,255,255,0.06);
  border-top: 1px solid rgba(255,255,255,0.1);
  padding: 1.2rem 2rem;
}
.hero-trust-bar-inner {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 1.5rem;
  flex-wrap: wrap;
}
.trust-badge-item img {
  height: 24px;
  width: auto;
  opacity: 0.85;
}
.trust-badge-divider {
  width: 1px;
  height: 24px;
  background: rgba(255,255,255,0.2);
}
.trust-badge-label {
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #94a3b8;
  white-space: nowrap;
}
.trust-badge-platforms {
  display: flex;
  align-items: center;
  gap: 1.2rem;
  flex-wrap: wrap;
}
.tb-platform {
  display: flex; align-items: center; gap: 0.4rem;
  font-size: 0.88rem; font-weight: 600; color: #cbd5e1;
  transition: color 0.2s;
}
.tb-platform:hover { color: #fff; }

/* ============================================================
   STATS BAR
   ============================================================ */
.stats-bar {
  background: #0f172a;
  padding: 3.5rem 2rem 2.5rem;
  text-align: center;
}
.stats-bar-inner {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 2rem;
}
.stat-item { text-align: center; }
.stat-big-num {
  font-size: 2.4rem;
  font-weight: 800;
  color: #fff;
  line-height: 1;
  margin-bottom: 0.4rem;
}
.stat-big-label {
  font-size: 0.82rem;
  color: #94a3b8;
  font-weight: 500;
  line-height: 1.4;
}
.stats-bar-quote {
  font-size: 0.95rem;
  color: #64748b;
  font-style: italic;
  margin-top: 2rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
}

/* ============================================================
   TRUST SECTION (with real photos)
   ============================================================ */
.trust-section {
  padding: 7rem 0;
  background: #fff;
}
.trust-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
}
.trust-card {
  background: linear-gradient(145deg, #ffffff 0%, #fafbfc 100%);
  border-radius: 24px;
  overflow: hidden;
  border: 1px solid rgba(226, 232, 240, 0.8);
  box-shadow: 0 6px 24px rgba(15, 23, 42, 0.06);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}
.trust-card:hover {
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.14);
  transform: translateY(-8px);
  border-color: rgba(40, 181, 193, 0.2);
}
.trust-card-img {
  width: 100%;
  height: 220px;
  overflow: hidden;
}
.trust-card-img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s;
}
.trust-card:hover .trust-card-img img {
  transform: scale(1.05);
}
.trust-card-body {
  padding: 2rem;
}
.trust-card-icon {
  width: 48px; height: 48px;
  border-radius: 12px;
  background: #e6f7f9;
  display: flex; align-items: center; justify-content: center;
  color: #28b5c1;
  font-size: 1.3rem;
  margin-bottom: 1rem;
}
.trust-card-body h3 {
  font-size: 1.15rem;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 0.6rem;
}
.trust-card-body p {
  font-size: 0.92rem;
  color: #64748b;
  line-height: 1.7;
}

/* ============================================================
   CAPABILITIES
   ============================================================ */
.capabilities {
  padding: 7rem 0;
  background: #f8fafc;
}
.capabilities-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
}
.cap-card {
  background: linear-gradient(145deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid rgba(226, 232, 240, 0.7);
  border-radius: 20px;
  padding: 2.2rem;
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}
.cap-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, transparent, rgba(40, 181, 193, 0.3), transparent);
  transform: scaleX(0);
  transition: transform 0.4s ease;
}
.cap-card:hover {
  box-shadow: 0 20px 48px rgba(15, 23, 42, 0.12);
  transform: translateY(-6px);
  border-color: rgba(40, 181, 193, 0.25);
}
.cap-card:hover::before {
  transform: scaleX(1);
}
.cap-card-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.2rem;
}
.cap-icon-wrap {
  width: 48px; height: 48px;
  border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  font-size: 1.3rem;
}
.cap-icon-wrap--0 { background: #d4f1f4; color: #1e9aa5; }
.cap-icon-wrap--1 { background: #dbeafe; color: #2563eb; }
.cap-icon-wrap--2 { background: #dcfce7; color: #16a34a; }
.cap-icon-wrap--3 { background: #fef3c7; color: #d97706; }
.cap-icon-wrap--4 { background: #fee2e2; color: #dc2626; }
.cap-icon-wrap--5 { background: #cffafe; color: #0891b2; }
.cap-num {
  font-size: 1.1rem;
  font-weight: 800;
  color: #e2e8f0;
}
.cap-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 0.65rem;
}
.cap-desc {
  font-size: 0.92rem;
  color: #64748b;
  line-height: 1.65;
  margin-bottom: 1.2rem;
}
.cap-link {
  font-size: 0.88rem;
  font-weight: 600;
  color: #28b5c1;
  text-decoration: none;
  transition: gap 0.2s;
}
.cap-link:hover { text-decoration: underline; }

/* ============================================================
   HOW IT WORKS
   ============================================================ */
.how-it-works {
  padding: 7rem 0;
  background: #fff;
}
.how-steps {
  display: flex;
  gap: 0;
  align-items: flex-start;
}
.how-step {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}
.step-num-wrap {
  display: flex;
  align-items: center;
  width: 100%;
  margin-bottom: 1.5rem;
  position: relative;
}
.step-num {
  width: 44px; height: 44px;
  border-radius: 50%;
  background: #28b5c1;
  color: #fff;
  font-size: 0.85rem;
  font-weight: 800;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  margin: 0 auto;
  position: relative;
  z-index: 1;
}
.step-line {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100%;
  height: 2px;
  background: linear-gradient(90deg, #c7d2fe, #e2e8f0);
  transform: translateY(-50%);
  z-index: 0;
}
.step-body { padding: 0 1rem; }
.step-icon-wrap {
  width: 52px; height: 52px;
  border-radius: 14px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 12px rgba(15,23,42,0.06);
  display: flex; align-items: center; justify-content: center;
  font-size: 1.4rem;
  color: #28b5c1;
  margin: 0 auto 1rem;
}
.step-title {
  font-size: 1rem;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 0.6rem;
}
.step-desc {
  font-size: 0.88rem;
  color: #64748b;
  line-height: 1.6;
}

/* ============================================================
   SOLUTIONS (with real images)
   ============================================================ */
.solutions-section {
  padding: 7rem 0;
  background: #f8fafc;
}
.solutions-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
}
.sol-card {
  background: #fff;
  border-radius: 20px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 20px rgba(15,23,42,0.06);
  transition: box-shadow 0.3s, transform 0.3s;
}
.sol-card:hover {
  box-shadow: 0 20px 50px rgba(15,23,42,0.12);
  transform: translateY(-6px);
}
.sol-card-img {
  width: 100%;
  height: 200px;
  overflow: hidden;
  background: #f1f5f9;
}
.sol-card-img img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 1.5rem;
  transition: transform 0.4s;
}
.sol-card:hover .sol-card-img img {
  transform: scale(1.05);
}
.sol-card-body {
  padding: 2rem;
}
.sol-badge {
  display: inline-block;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #28b5c1;
  background: #e6f7f9;
  border-radius: 999px;
  padding: 0.25rem 0.8rem;
  margin-bottom: 0.8rem;
}
.sol-badge--blue { color: #2563eb; background: #dbeafe; }
.sol-badge--green { color: #16a34a; background: #dcfce7; }
.sol-title {
  font-size: 1.15rem;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 0.7rem;
}
.sol-desc {
  font-size: 0.9rem;
  color: #64748b;
  line-height: 1.65;
  margin-bottom: 1.4rem;
}
.sol-cta {
  font-size: 0.9rem;
  font-weight: 600;
  color: #28b5c1;
  text-decoration: none;
  display: inline-flex; align-items: center; gap: 0.3rem;
  transition: gap 0.2s;
}
.sol-cta:hover span { margin-left: 4px; }

/* ============================================================
   INDUSTRY EXPERTISE (with real images)
   ============================================================ */
.industry-section {
  padding: 7rem 0;
  background: #fff;
}
.industry-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
}
.industry-card {
  background: #fff;
  border-radius: 20px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 20px rgba(15,23,42,0.06);
  transition: box-shadow 0.3s, transform 0.3s;
}
.industry-card:hover {
  box-shadow: 0 20px 50px rgba(15,23,42,0.12);
  transform: translateY(-6px);
}
.industry-card-img {
  width: 100%;
  height: 180px;
  overflow: hidden;
  position: relative;
  background: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: center;
}
.industry-card-img img {
  height: 120px;
  width: auto;
  object-fit: contain;
  transition: transform 0.4s;
}
.industry-card:hover .industry-card-img img {
  transform: scale(1.08);
}
.industry-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 0.8rem 1.2rem;
  background: linear-gradient(transparent, rgba(15,23,42,0.7));
}
.industry-tag {
  font-size: 0.8rem;
  font-weight: 700;
  color: #fff;
}
.industry-card-body {
  padding: 1.5rem 2rem 2rem;
}
.industry-card-body h4 {
  font-size: 1.1rem;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 0.5rem;
}
.industry-card-body p {
  font-size: 0.9rem;
  color: #64748b;
  line-height: 1.65;
  margin-bottom: 1rem;
}
.industry-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}
.industry-tags span {
  font-size: 0.72rem;
  font-weight: 600;
  color: #28b5c1;
  background: #e6f7f9;
  border-radius: 999px;
  padding: 0.2rem 0.65rem;
}

/* ============================================================
   OPERATING SYSTEM (with dashboard mockup)
   ============================================================ */
.os-section {
  padding: 7rem 0;
  background: linear-gradient(160deg, #0f172a 0%, #1e1b4b 100%);
}
.os-section .section-eyebrow { background: rgba(255,255,255,0.08); color: #a5b4fc; }
.os-section .section-title { color: #fff; }
.os-section .section-desc { color: #94a3b8; }
.os-showcase {
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 3rem;
  align-items: start;
}

/* Dashboard Mockup */
.os-dashboard-mockup {
  display: grid;
  grid-template-columns: 60px 1fr;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 32px 80px rgba(0,0,0,0.4);
  border: 1px solid rgba(255,255,255,0.08);
  background: #0f172a;
}
.dash-sidebar {
  background: #0c1222;
  padding: 12px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  border-right: 1px solid rgba(255,255,255,0.06);
}
.dash-logo {
  width: 36px; height: 36px;
  border-radius: 10px;
  background: linear-gradient(135deg, #28b5c1, #1e9aa5);
  display: flex; align-items: center; justify-content: center;
  margin-bottom: 16px;
  padding: 2px;
}
.dash-nav { display: flex; flex-direction: column; gap: 2px; width: 100%; }
.dash-nav-item {
  font-size: 0;
  padding: 10px 0;
  text-align: center;
  color: #475569;
  cursor: default;
  transition: color 0.2s, background 0.2s;
  border-radius: 0;
}
.dash-nav-item i { font-size: 0.85rem; }
.dash-nav-item.active { color: #a5b4fc; background: rgba(40,181,193,0.15); border-right: 2px solid #818cf8; }

.dash-main {
  padding: 0;
  min-height: 0;
  background: #111827;
}
.dash-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  border-bottom: 1px solid rgba(255,255,255,0.06);
  background: rgba(255,255,255,0.02);
}
.dash-breadcrumb {
  font-size: 0.72rem;
  font-weight: 600;
  color: #e2e8f0;
}
.dash-breadcrumb span { color: #64748b; font-weight: 400; }
.dash-topbar-right {
  display: flex;
  align-items: center;
  gap: 12px;
}
.dash-search, .dash-notif {
  color: #64748b;
  font-size: 0.78rem;
  position: relative;
}
.dash-notif .notif-dot {
  position: absolute;
  top: -2px; right: -2px;
  width: 6px; height: 6px;
  background: #ef4444;
  border-radius: 50%;
}
.dash-avatar {
  width: 24px; height: 24px;
  border-radius: 50%;
  background: linear-gradient(135deg, #28b5c1, #1e9aa5);
  color: #fff;
  font-size: 0.62rem;
  font-weight: 700;
  display: flex; align-items: center; justify-content: center;
}

.dash-stats-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  padding: 12px 16px;
}
.dash-stat-card {
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 10px;
  padding: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
}
.dash-stat-icon {
  width: 28px; height: 28px;
  border-radius: 7px;
  display: flex; align-items: center; justify-content: center;
  font-size: 0.72rem;
  flex-shrink: 0;
}
.dash-stat--0 .dash-stat-icon { background: rgba(40,181,193,0.25); color: #a5b4fc; }
.dash-stat--1 .dash-stat-icon { background: rgba(34,197,94,0.25); color: #86efac; }
.dash-stat--2 .dash-stat-icon { background: rgba(14,165,233,0.25); color: #7dd3fc; }
.dash-stat--3 .dash-stat-icon { background: rgba(249,115,22,0.25); color: #fdba74; }
.dash-stat-value {
  font-size: 0.88rem;
  font-weight: 800;
  color: #f1f5f9;
  line-height: 1;
}
.dash-stat-label {
  font-size: 0.55rem;
  color: #64748b;
  margin-top: 2px;
  line-height: 1;
}
.dash-stat-trend {
  font-size: 0.55rem;
  font-weight: 700;
  margin-left: auto;
  white-space: nowrap;
}
.dash-stat-trend.up { color: #22c55e; }
.dash-stat-trend.down { color: #ef4444; }

.dash-content-row {
  display: grid;
  grid-template-columns: 1.4fr 1fr;
  gap: 8px;
  padding: 0 16px;
}
.dash-chart-panel, .dash-status-panel {
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 10px;
  padding: 12px;
}
.dash-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.62rem;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 10px;
}
.dash-panel-badge {
  font-size: 0.5rem;
  background: rgba(34,197,94,0.2);
  color: #22c55e;
  padding: 2px 6px;
  border-radius: 4px;
  text-transform: none;
  letter-spacing: 0;
}
.dash-chart-svg {
  width: 100%;
  height: auto;
}
.dash-status-list { display: flex; flex-direction: column; gap: 8px; }
.dash-status-item {
  display: flex;
  align-items: center;
  gap: 8px;
}
.dash-status-dot {
  width: 7px; height: 7px;
  border-radius: 2px;
  flex-shrink: 0;
}
.dash-status-label {
  font-size: 0.62rem;
  color: #cbd5e1;
  flex: 0 0 58px;
}
.dash-status-pct {
  font-size: 0.62rem;
  font-weight: 700;
  color: #f1f5f9;
  width: 26px;
}
.dash-status-bar-bg {
  flex: 1;
  height: 4px;
  background: rgba(255,255,255,0.06);
  border-radius: 2px;
  overflow: hidden;
}
.dash-status-bar-fill {
  height: 100%;
  border-radius: 2px;
  transition: width 0.6s ease;
}

.dash-recent-row {
  padding: 8px 16px 12px;
}
.dash-recent-panel {
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 10px;
  padding: 12px;
}
.dash-recent-list { display: flex; flex-direction: column; gap: 6px; }
.dash-order-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 8px;
  background: rgba(255,255,255,0.02);
  border-radius: 6px;
}
.dash-order-id {
  font-size: 0.62rem;
  font-weight: 700;
  color: #a5b4fc;
  width: 64px;
}
.dash-order-sku {
  font-size: 0.58rem;
  color: #94a3b8;
  flex: 1;
}
.dash-order-status {
  font-size: 0.52rem;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 4px;
  white-space: nowrap;
}
.dash-order-status.status-shipped { background: rgba(59,130,246,0.15); color: #93c5fd; }
.dash-order-status.status-packing { background: rgba(249,115,22,0.15); color: #fdba74; }
.dash-order-status.status-qc { background: rgba(168,85,247,0.15); color: #c4b5fd; }
.dash-order-status.status-delivered { background: rgba(34,197,94,0.15); color: #86efac; }

.os-showcase-features {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}
.os-feature-item {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  padding: 1.2rem;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 14px;
  transition: background 0.2s, border-color 0.2s;
}
.os-feature-item:hover {
  background: rgba(255,255,255,0.08);
  border-color: rgba(165,180,252,0.3);
}
.os-feature-icon {
  width: 44px; height: 44px;
  border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
  font-size: 1.1rem;
  flex-shrink: 0;
}
.os-feature-icon--0 { background: rgba(124,58,237,0.25); color: #c4b5fd; }
.os-feature-icon--1 { background: rgba(14,165,233,0.25); color: #7dd3fc; }
.os-feature-icon--2 { background: rgba(34,197,94,0.25); color: #86efac; }
.os-feature-icon--3 { background: rgba(249,115,22,0.25); color: #fdba74; }
.os-feature-icon--4 { background: rgba(239,68,68,0.25); color: #fca5a5; }
.os-feature-icon--5 { background: rgba(20,184,166,0.25); color: #5eead4; }
.os-feature-content h4 {
  font-size: 0.95rem;
  font-weight: 700;
  color: #f1f5f9;
  margin-bottom: 0.3rem;
}
.os-feature-content p {
  font-size: 0.85rem;
  color: #94a3b8;
  line-height: 1.5;
  margin-bottom: 0.5rem;
}
.os-highlight-row {
  display: flex; flex-wrap: wrap; gap: 0.4rem;
}
.os-highlight {
  font-size: 0.72rem;
  font-weight: 600;
  color: #a5b4fc;
  background: rgba(165,180,252,0.12);
  border: 1px solid rgba(165,180,252,0.25);
  border-radius: 999px;
  padding: 0.2rem 0.65rem;
}

/* ============================================================
   CASE STUDY
   ============================================================ */
.case-section {
  padding: 7rem 0;
  background: #f8fafc;
}
.case-card {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 24px;
  padding: 3rem;
  box-shadow: 0 8px 40px rgba(15,23,42,0.08);
  overflow: hidden;
  position: relative;
}
.case-card::before {
  content: '';
  position: absolute;
  top: 0; left: 0; right: 0;
  height: 4px;
  background: linear-gradient(90deg, #28b5c1, #06b6d4);
}
.case-badge {
  display: inline-block;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #16a34a;
  background: #dcfce7;
  border-radius: 999px;
  padding: 0.3rem 0.8rem;
  margin-bottom: 1rem;
}
.case-title {
  font-size: 1.6rem;
  font-weight: 800;
  color: #0f172a;
  margin-bottom: 1rem;
  line-height: 1.25;
}
.case-intro {
  font-size: 0.95rem;
  color: #475569;
  line-height: 1.7;
  margin-bottom: 1.5rem;
}
.case-factors-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 0.8rem;
}
.case-factors-list {
  list-style: none;
  padding: 0;
  margin-bottom: 2rem;
}
.case-factors-list li {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  font-size: 0.88rem;
  color: #475569;
  line-height: 1.5;
  margin-bottom: 0.5rem;
}
.case-img-wrap {
  width: 120px;
  height: 120px;
  margin: 0 auto 1.5rem;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(15,23,42,0.1);
}
.case-img-wrap img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 0.5rem;
}
.case-results-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 1rem;
}
.case-results-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 2rem;
}
.case-result {
  background: #f8fafc;
  border-radius: 14px;
  padding: 1.2rem 1rem;
  text-align: center;
  border: 1px solid #e2e8f0;
}
.case-result-num {
  font-size: 2rem;
  font-weight: 800;
  color: #28b5c1;
  line-height: 1;
  margin-bottom: 0.3rem;
}
.case-result-label {
  font-size: 0.78rem;
  color: #64748b;
  line-height: 1.3;
}
.case-testimonial {
  background: #f0f4ff;
  border-left: 4px solid #28b5c1;
  border-radius: 0 12px 12px 0;
  padding: 1.5rem;
}
.case-quote {
  font-size: 0.92rem;
  color: #334155;
  line-height: 1.7;
  font-style: italic;
  margin-bottom: 1rem;
}
.case-author {
  display: flex; align-items: center; gap: 0.8rem;
}
.case-author-avatar {
  width: 38px; height: 38px;
  border-radius: 50%;
  background: linear-gradient(135deg, #28b5c1, #1e9aa5);
  color: #fff;
  font-size: 1rem;
  font-weight: 700;
  display: flex; align-items: center; justify-content: center;
}
.case-author-name {
  font-size: 0.9rem;
  font-weight: 700;
  color: #0f172a;
}
.case-author-role {
  font-size: 0.78rem;
  color: #64748b;
}

/* ============================================================
   INTEGRATIONS (with real carousel images)
   ============================================================ */
.integrations-section {
  padding: 7rem 0;
  background: #f8fafc;
  overflow: hidden;
}
.integrations-carousel {
  overflow: hidden;
  mask-image: linear-gradient(to right, transparent, black 8%, black 92%, transparent);
}
.carousel-track {
  display: flex;
  gap: 1.5rem;
  animation: trackScroll 30s linear infinite;
  width: max-content;
}
@keyframes trackScroll {
  from { transform: translateX(0); }
  to { transform: translateX(-50%); }
}
.carousel-item {
  width: 280px;
  height: 280px;
  border-radius: 20px;
  overflow: hidden;
  flex-shrink: 0;
  box-shadow: 0 8px 24px rgba(15,23,42,0.08);
  border: 1px solid #e2e8f0;
}
.carousel-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* ============================================================
   TESTIMONIALS
   ============================================================ */
.testimonials-section {
  padding: 7rem 0;
  background: #fff;
}
.testimonials-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
}
.testimonial-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(15,23,42,0.06);
  transition: box-shadow 0.3s, transform 0.3s;
}
.testimonial-card:hover {
  box-shadow: 0 16px 40px rgba(15,23,42,0.1);
  transform: translateY(-4px);
}
.testimonial-stars {
  color: #fbbf24;
  font-size: 0.85rem;
  margin-bottom: 1rem;
  display: flex;
  gap: 0.15rem;
}
.testimonial-text {
  font-size: 0.95rem;
  color: #334155;
  line-height: 1.7;
  margin-bottom: 1.5rem;
  font-style: italic;
}
.testimonial-author {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.testimonial-avatar {
  width: 40px; height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #28b5c1, #1e9aa5);
  color: #fff;
  font-size: 0.95rem;
  font-weight: 700;
  display: flex; align-items: center; justify-content: center;
}
.testimonial-name {
  font-size: 0.9rem;
  font-weight: 700;
  color: #0f172a;
}
.testimonial-role {
  font-size: 0.78rem;
  color: #64748b;
}

/* ============================================================
   PARTNERS
   ============================================================ */
.partners-section {
  padding: 6rem 0;
  background: #f8fafc;
  overflow: hidden;
}
.partners-track-wrap {
  overflow: hidden;
  mask-image: linear-gradient(to right, transparent, black 10%, black 90%, transparent);
}
.partners-track {
  display: flex;
  gap: 1.5rem;
  animation: trackScroll 24s linear infinite;
  width: max-content;
}
.partner-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 1.2rem 2rem;
  min-width: 140px;
  font-size: 0.9rem;
  font-weight: 600;
  color: #64748b;
  white-space: nowrap;
  transition: border-color 0.2s, color 0.2s;
}
.partner-icon-wrap {
  font-size: 2rem;
  color: #94a3b8;
}

/* ============================================================
   CTA SECTION
   ============================================================ */
.cta-section {
  padding: 7rem 2rem;
  background: linear-gradient(135deg, #28b5c1 0%, #1e9aa5 60%, #06b6d4 100%);
  text-align: center;
  position: relative;
  overflow: hidden;
}
.cta-inner { max-width: 700px; margin: 0 auto; position: relative; z-index: 1; }
.cta-decoration {
  position: absolute;
  top: 1rem;
  right: -2rem;
  opacity: 0.15;
}
.cta-decoration img { height: 80px; width: auto; }
.cta-title {
  font-size: 2.6rem;
  font-weight: 800;
  color: #fff;
  line-height: 1.2;
  margin-bottom: 1rem;
}
.cta-sub {
  font-size: 1.05rem;
  color: rgba(255,255,255,0.8);
  margin-bottom: 2.5rem;
}
.cta-btn-group {
  display: flex; justify-content: center; flex-wrap: wrap; gap: 1rem;
}
.cta-section .btn-primary {
  background: #fff;
  color: #28b5c1;
  box-shadow: 0 4px 20px rgba(0,0,0,0.2);
}
.cta-section .btn-primary:hover { background: #f0f4ff; }
.cta-section .btn-ghost { border-color: rgba(255,255,255,0.5); color: #fff; }
.cta-section .btn-ghost:hover { background: rgba(255,255,255,0.1); border-color: #fff; color: #fff; }

/* ============================================================
   CONTACT
   ============================================================ */
.contact {
  padding: 7rem 0;
  background: #fff;
}
.contact-layout {
  display: grid;
  grid-template-columns: 3fr 2fr;
  gap: 3rem;
}
.contact-form-box {
  background: #f8fafc;
  border-radius: 20px;
  padding: 2.5rem;
  border: 1px solid #e2e8f0;
}
.contact-form-title {
  font-size: 1.4rem;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 0.5rem;
}
.contact-form-text {
  font-size: 0.9rem;
  color: #64748b;
  margin-bottom: 2rem;
}
.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}
.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-bottom: 1.2rem;
}
.form-group label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #374151;
}
.form-group input,
.form-group textarea {
  padding: 0.8rem 1rem;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  font-size: 0.95rem;
  color: #0f172a;
  background: #fff;
  transition: border-color 0.2s, box-shadow 0.2s;
  font-family: inherit;
}
.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #28b5c1;
  box-shadow: 0 0 0 3px rgba(40,181,193,0.1);
}
.form-group textarea {
  resize: vertical;
  min-height: 140px;
}
.contact-info-box {
  background: #0f172a;
  border-radius: 20px;
  padding: 2.5rem;
  color: #fff;
}
.contact-info-title {
  font-size: 1.2rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 1.5rem;
}
.contact-info-list {
  list-style: none;
  padding: 0;
  margin-bottom: 2rem;
}
.contact-info-list li {
  display: flex;
  align-items: flex-start;
  gap: 0.8rem;
  margin-bottom: 1rem;
  font-size: 0.9rem;
  color: #94a3b8;
  line-height: 1.5;
}
.contact-info-icon {
  width: 32px; height: 32px;
  border-radius: 8px;
  background: rgba(255,255,255,0.08);
  display: flex; align-items: center; justify-content: center;
  color: #a5b4fc;
  font-size: 0.85rem;
  flex-shrink: 0;
}
.contact-features { border-top: 1px solid rgba(255,255,255,0.1); padding-top: 1.5rem; }
.contact-feature {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: #94a3b8;
  margin-bottom: 0.7rem;
}

/* ============================================================
   RESPONSIVE
   ============================================================ */
@media (max-width: 1024px) {
  .capabilities-grid,
  .solutions-grid,
  .trust-grid,
  .industry-grid { grid-template-columns: repeat(2, 1fr); }
  .os-showcase { grid-template-columns: 1fr; }
  .os-dashboard-mockup { grid-template-columns: 1fr; }
  .dash-sidebar { display: none; }
  .dash-stats-row { grid-template-columns: repeat(2, 1fr); }
  .stats-bar-inner { grid-template-columns: repeat(3, 1fr); }
  .hero-body { gap: 2rem; }
  .hero-visual { flex: 0 0 400px; }
  .testimonials-grid { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 768px) {
  .hero-body { flex-direction: column; }
  .hero-visual { flex: none; width: 100%; }
  .hero-float-card { display: none; }
  .capabilities-grid,
  .solutions-grid,
  .trust-grid,
  .industry-grid,
  .who-grid,
  .testimonials-grid { grid-template-columns: 1fr; }
  .stats-bar-inner { grid-template-columns: repeat(2, 1fr); }
  .how-steps { flex-direction: column; gap: 2rem; }
  .step-line { display: none; }
  .case-card { grid-template-columns: 1fr; }
  .contact-layout { grid-template-columns: 1fr; }
  .form-row { grid-template-columns: 1fr; }
  .section-title { font-size: 2rem; }
  .cta-title { font-size: 1.9rem; }
  .hero-trust-bar-inner { flex-wrap: wrap; }
  .dash-stats-row { grid-template-columns: 1fr 1fr; }
  .dash-content-row { grid-template-columns: 1fr; }
  .dash-stat-card { flex-wrap: wrap; }
}
@media (max-width: 480px) {
  .stats-bar-inner { grid-template-columns: 1fr 1fr; }
  .hero-title-2 { font-size: 2.2rem; }
}
</style>
